'use client';
/**
 * HeroVideo.tsx — Client Component island
 * Renders the hero background video.
 *
 * Cycle 83: requestIdleCallback removed — el.src is set immediately on mount
 * so the browser starts fetching the video as soon as JS hydrates, eliminating
 * the 2–4 s dark-hero delay Lars observed on staging.
 *
 * A <link rel="preload" as="video"> in page.tsx additionally allows the browser
 * to start fetching the video in parallel with CSS/fonts, before JS even mounts.
 *
 * - No autoplay attribute — video.play() is called from JS after src is set
 * - No poster — .hero-fallback CSS gradient div is the visual fallback
 * - No prefers-reduced-motion check (per brief)
 *
 * Video CDN: Cloudflare R2 (pf-website-media bucket, WEUR region)
 * Migrated from GoHighLevel/filesafe.space on 2026-06-17 (GHL retirement).
 */

import { useEffect, useRef } from 'react';

const HERO_VIDEO_SRC =
  'https://pub-a23e7f5cd7f54af9858eee0eedce9cf5.r2.dev/video/hero-background-v2.mp4';

export default function HeroVideo() {
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    const el = videoRef.current;
    if (!el) return;
    el.src = HERO_VIDEO_SRC;
    el.load();
    el.play().catch(() => {
      // Autoplay blocked by browser — video stays paused, no error surfaced
    });
  }, []);

  return (
    <video
      ref={videoRef}
      className="hero-video"
      muted
      loop
      playsInline
      preload="none"
      aria-hidden="true"
    />
  );
}
